import { useEffect, useMemo, useState } from 'react'
import { useAuth } from './context/useAuth'
import { useI18n } from './context/useI18n'
import { useLocation, useNavigate } from 'react-router-dom'
import ModernLogin from './components/ModernLogin'
import RegisterCommercialWizard from './components/RegisterCommercialWizard'
import Dashboard from './components/Dashboard'
import PasswordRecoveryPage from './components/PasswordRecoveryPage'
import {
  parseGymPublicPath,
  readOrgSlugFromQuery,
} from './utils/gymPublicPath'
import DemoApp from './demo/DemoApp'
import FitLivePublicLanding from './fit-live/FitLivePublicLanding'
import { isFitLivePublicPath, isFitLiveUiEnabled, isTrainingPublicHost } from './utils/fitLiveModule'

/** Registro oficial único (wizard comercial). Requiere VITE_REGISTER_WIZARD_V2=true en prod. */
const registerWizardEnabled = import.meta.env.VITE_REGISTER_WIZARD_V2 !== 'false'

function readInviteQuery(search) {
  try {
    const q = new URLSearchParams(typeof search === 'string' ? search : '')
    const code = String(q.get('code') || '').trim()
    if (!code) return false
    const entry = String(q.get('entry') || '').trim().toLowerCase()
    // QR sede/gym: ?code=GYM-…&entry=invite (entry opcional por compat).
    return !entry || entry === 'invite'
  } catch {
    return false
  }
}

/** Landing D28D: /login?register=1 → abrir wizard (no pantalla de login). */
function readDirectRegisterQuery(search) {
  try {
    const q = new URLSearchParams(typeof search === 'string' ? search : '')
    if (String(q.get('code') || '').trim()) return false
    const flag = String(q.get('register') || '').trim().toLowerCase()
    return flag === '1' || flag === 'true'
  } catch {
    return false
  }
}

/** Rutas legacy que pintaban ModernLogin oscuro en :3002 (retiradas con Fit Live). */
function isRetiredDarkAuthPath(pathname = '') {
  const p = String(pathname || '').replace(/\/+$/, '') || '/'
  return p === '/access' || p === '/signin' || p === '/sign-in' || p === '/entrar'
}

/**
 * ModernLogin oscuro solo para puertas Training / SSO / org coach.
 * En plataforma Fit Live (:3002) el login va por landing + modal crema.
 */
function needsShellModernLogin(search, { legacyOrg } = {}) {
  if (isTrainingPublicHost()) return true
  try {
    const q = new URLSearchParams(typeof search === 'string' ? search : '')
    if (q.has('entry_sso')) return true
    if (String(q.get('service') || '').toLowerCase() === 'training') return true
  } catch { /* noop */ }
  return Boolean(legacyOrg)
}

function App() {
  const { user, loading } = useAuth()
  const { t } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const inviteFromQuery = useMemo(() => readInviteQuery(location.search), [location.search])
  const directRegisterFromQuery = useMemo(() => readDirectRegisterQuery(location.search), [location.search])
  const [showRegister, setShowRegister] = useState(() => inviteFromQuery || directRegisterFromQuery)
  const [registerEntryMode, setRegisterEntryMode] = useState(() => (inviteFromQuery ? 'invite' : 'direct'))

  const gymPublic = parseGymPublicPath(location.pathname)
  const legacyOrg = readOrgSlugFromQuery(location.search)

  // QR / enlace invitación: ?code=…&entry=invite → abrir wizard de registro (no login).
  useEffect(() => {
    if (user || loading) return
    if (gymPublic) return
    if (!inviteFromQuery) return
    setRegisterEntryMode('invite')
    setShowRegister(true)
  }, [user, loading, gymPublic, inviteFromQuery])

  // Landing: ?register=1&service=d28d&plan=basico → wizard directo (sin código).
  useEffect(() => {
    if (user || loading) return
    if (gymPublic) return
    if (inviteFromQuery || !directRegisterFromQuery) return
    setRegisterEntryMode('direct')
    setShowRegister(true)
  }, [user, loading, gymPublic, inviteFromQuery, directRegisterFromQuery])

  // /gym/:slug es puerta de gym virtual RETIRADA. Coach (Jabel, etc.) → /login?org=&service=training.
  // Sesión ya abierta: nunca dejar al usuario en login/landing D28D gym.
  useEffect(() => {
    if (loading) return
    if (!gymPublic) return
    if (user) {
      navigate('/', { replace: true })
      return
    }
    const params = new URLSearchParams(location.search)
    params.set('org', gymPublic.slug)
    if (!params.get('service')) params.set('service', 'training')
    if (gymPublic.action === 'register') {
      if (gymPublic.entryMode === 'invite') params.set('entry', 'invite')
      else params.set('register', '1')
    }
    const qs = params.toString()
    navigate(`/login?${qs}`, { replace: true })
  }, [user, loading, gymPublic, location.search, navigate])

  // Ya no redirigir /login?org= → /gym/.../login (front Gym Virtual retirado).

  // Sin sesión, /dashboard no debe pintar login oscuro → landing Fit Live (o /login si flag off).
  useEffect(() => {
    if (user || loading) return
    if (location.pathname !== '/dashboard' && !location.pathname.startsWith('/dashboard/')) return
    if (isFitLiveUiEnabled() && !isTrainingPublicHost()) {
      navigate(`/${location.search || ''}`, { replace: true })
      return
    }
    navigate(`/login${location.search || ''}`, { replace: true })
  }, [user, loading, location.pathname, location.search, navigate])

  useEffect(() => {
    if (loading || !user) return
    if (location.pathname !== '/login' && !isRetiredDarkAuthPath(location.pathname)) return
    if (new URLSearchParams(location.search).has('entry_sso')) return
    navigate('/', { replace: true })
  }, [user, loading, location.pathname, location.search, navigate])

  // Fit Live: /login y rutas legacy (/access, etc.) de plataforma → landing / (modal crema).
  // Conservar ModernLogin solo para Training SSO / org / service=training.
  useEffect(() => {
    if (loading || user) return
    if (!isFitLiveUiEnabled() || isTrainingPublicHost()) return
    if (inviteFromQuery || directRegisterFromQuery) return
    if (needsShellModernLogin(location.search, { legacyOrg })) return
    const path = location.pathname
    if (path !== '/login' && !isRetiredDarkAuthPath(path)) return
    navigate('/', { replace: true })
  }, [user, loading, location.pathname, location.search, navigate, legacyOrg, inviteFromQuery, directRegisterFromQuery])

  if (location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
    return <PasswordRecoveryPage />
  }

  // Simulador público Food + Training (sin login; datos solo en sessionStorage).
  if (location.pathname === '/demo' || location.pathname.startsWith('/demo/')) {
    return <DemoApp />
  }

  // Fit Live público (modelo web): explorar sin registro. /live · /fit-live · / (si flag).
  const fitLivePublicEntry = isFitLiveUiEnabled() && !isTrainingPublicHost() && (
    isFitLivePublicPath(location.pathname)
    || (
      (location.pathname === '/' || location.pathname === '')
      && !inviteFromQuery
      && !directRegisterFromQuery
      && !legacyOrg
      && !gymPublic
    )
  );

  if (gymPublic && !user) {
    return <div className="loading">{t('common.loading', 'Cargando...')}</div>
  }

  if (loading) {
    return <div className="loading">{t('common.loading', 'Cargando...')}</div>
  }

  if (!user) {
    if (showRegister) {
      if (!registerWizardEnabled) {
        return (
          <div className="loading">
            Registro no disponible. Contacta al administrador de la plataforma.
          </div>
        )
      }
      return (
        <RegisterCommercialWizard
          initialEntryMode={registerEntryMode}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setRegisterEntryMode('direct')
          }}
        />
      )
    }

    // Evitar pintar ModernLogin en /dashboard o /access mientras redirige a landing.
    if (
      location.pathname === '/dashboard'
      || location.pathname.startsWith('/dashboard/')
      || isRetiredDarkAuthPath(location.pathname)
    ) {
      if (isFitLiveUiEnabled() && !isTrainingPublicHost() && !needsShellModernLogin(location.search, { legacyOrg })) {
        return <FitLivePublicLanding />
      }
      return <div className="loading">{t('common.loading', 'Cargando...')}</div>
    }

    // Con Fit Live: nunca ModernLogin oscuro en :3002 (landing + modal crema).
    // ModernLogin solo Training: entry_sso / service=training / ?org= coach.
    const allowModernLogin = needsShellModernLogin(location.search, { legacyOrg })
    if (isFitLiveUiEnabled() && !isTrainingPublicHost() && !allowModernLogin) {
      return <FitLivePublicLanding />
    }
    // Flag off: conservar landing pública /live si aplica.
    if (fitLivePublicEntry) {
      return <FitLivePublicLanding />
    }

    return (
      <ModernLogin
        onForgotPassword={() => navigate('/forgot-password')}
        onSwitchToRegister={() => {
          setRegisterEntryMode('direct')
          setShowRegister(true)
        }}
        onSwitchToRegisterWithCode={() => {
          setRegisterEntryMode('invite')
          setShowRegister(true)
        }}
      />
    )
  }

  return <Dashboard />
}

export default App
